import { Alert, Button, Snackbar } from '@mui/material'
import { NoteField } from './NoteField'
import _ from 'lodash'
import Flower from './Flower'
import HighFiveDialog from './HighFiveDialog'
import { MiwDialog } from './MiwDialog'
import { CSSProperties, useState } from 'react'
import HabitsPanel from './HabitsPanel'
import { HighFiveText } from './HighFiveText'
import { Moment } from 'moment'
import { TeammateTargetsDialog } from './TeammateTargetsDialog'
import {
  MiwProgress,
  makeHabitsFromTargetsAndScores,
} from '../utils/dataFormatting'
import { DashboardData } from '../api'
import { Streak } from '../utils/calcStreak'
import { HighFiveContent } from './ChatPage'
import { highFiveValue, regularTargetValue } from '../utils/constants'

const scoreSectionStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '20px 0px',
}

const footerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}

const footerButtonDivStyle: CSSProperties = {
  marginTop: '5px',
  marginBottom: '5px',
  display: 'flex',
  alignItems: 'center',
}

const highFiveButtonsStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
}

export type OldHabitType =
  | 'Sleep'
  | 'Exercise'
  | 'Nutrition'
  | 'Planned Day'
  | 'Main Priority'
  | 'Personal Challenge'
  | 'Love'

export type NewHabitType =
  | 'sleep'
  | 'fitness'
  | 'nutrition'
  | 'plannedDay'
  | 'miw'
  | 'personalChallenge'
  | 'love'

export interface Habit {
  scoreId?: string
  name: OldHabitType
  score: number
  target: string
  miwProgress?: MiwProgress
  miwStarterTarget?: number
  miwMainTarget?: number
  scoreDay?: Moment
}

export interface MiwHabit extends Habit {
  miwProgress: MiwProgress
  miwStarterTarget: number
  miwMainTarget: number
}

export function isMiwHabit(habit?: Habit): habit is MiwHabit {
  return habit?.name === 'Main Priority'
}

export interface TargetScoresProps {
  dashboardData: DashboardData
  streaks: Streak[]
  day?: Moment
  onClickHabit?: Function
  onSetMiwProgress?: (habit: MiwHabit) => Promise<void>
  onSaveNotes?: Function
  onSendHighFive?: Function
  isViewOnly?: boolean
}

export const TargetScores = ({
  onClickHabit,
  onSetMiwProgress,
  onSaveNotes,
  onSendHighFive,
  isViewOnly,
  ...props
}: TargetScoresProps) => {
  const handlers = {
    onClickHabit,
    onSetMiwProgress,
    onSaveNotes,
    onSendHighFive,
  }
  const enabledHandlers = !isViewOnly
    ? handlers
    : _.mapValues(handlers, () => async () => _.noop())

  return (
    <TargetScoresBase {...props} {...enabledHandlers} isViewOnly={isViewOnly} />
  )
}

const TargetScoresBase = ({
  dashboardData,
  streaks,
  onClickHabit,
  onSetMiwProgress,
  onSaveNotes,
  onSendHighFive,
  isViewOnly,
  day,
}: TargetScoresProps) => {
  const {
    player,
    targets,
    scores,
    notes,
    highFives,
    teammate,
    teammateScores,
  } = dashboardData
  const habits = makeHabitsFromTargetsAndScores(targets, scores)
  const highFivesSentToday = highFives.filter((h) => h.day.isSame(day, 'day'))
  const playerHasReceivedHighFiveToday = highFivesSentToday.some(
    (h) => h.recipientId === player.id
  )
  const playerHasSentHighFiveToday = highFivesSentToday.some(
    (h) => h.senderId === player.id
  )

  const score =
    _(habits)
      .map((h) => h.score)
      .sum() + (playerHasReceivedHighFiveToday ? highFiveValue : 0)

  const teammateScore =
    _(teammateScores)
      .map((s) => s.points)
      .sum() + (playerHasSentHighFiveToday ? highFiveValue : 0)

  const miwHabit = habits.find((h) => h.name === 'Main Priority')

  const [activeHabit, setActiveHabit] = useState<OldHabitType | undefined>()
  const [isShowingHighFiveDialog, setIsShowingHighFiveDialog] = useState(false)
  const [isShowingMiwDialog, setIsShowingMiwDialog] = useState(false)
  const [isShowingConfejji, setIsShowingConfejji] = useState(false)
  const [isShowingNotification, setIsShowingNotification] = useState(false)
  const [isShowingTeammateDialog, setIsShowingTeammateDialog] = useState(false)
  const [notificationText, setNotificationText] = useState(null)

  const showConfejji = () => {
    setIsShowingConfejji(true)
    setTimeout(() => setIsShowingConfejji(false), 4000)
  }

  const showNotification = (text: any) => {
    setIsShowingNotification(true)
    setNotificationText(text)
  }

  const handleCloseNotification = () => {
    setIsShowingNotification(false)
  }

  const handleClickHabit = async (habit: Habit) => {
    setActiveHabit(habit.name)

    if (habit.name === 'Main Priority') {
      setIsShowingMiwDialog(true)
    } else {
      if (habit.score < regularTargetValue) {
        showConfejji()
      } else {
        setIsShowingConfejji(false)
      }
      onClickHabit && (await onClickHabit(habit))
    }
  }

  const handleSendHighFive = async (highFiveContent: HighFiveContent) => {
    showNotification('Sending high five!')
    setIsShowingHighFiveDialog(false)
    onSendHighFive && (await onSendHighFive(highFiveContent))
    showNotification('High five sent!')
  }

  const handleMiwSubmit = async (progress: MiwProgress) => {
    const habit = habits.find(isMiwHabit)
    if (habit && onSetMiwProgress) {
      setIsShowingMiwDialog(false)
      if (progress !== 'none') {
        showConfejji()
      }
      await onSetMiwProgress({ ...habit, miwProgress: progress })
    }
  }

  const handleSaveNotes = async (text: any) => {
    onSaveNotes && (await onSaveNotes(text))
    showNotification('Notes saved!')
  }

  return (
    <div>
      <HabitsPanel
        habits={habits}
        targets={targets}
        streaks={streaks}
        isViewOnly={isViewOnly}
        onClickHabit={handleClickHabit}
        activeHabit={activeHabit}
        isShowingConfejji={isShowingConfejji}
        playerHasSentHighFiveToday={playerHasSentHighFiveToday}
        onClickHighFive={() => setIsShowingHighFiveDialog(true)}
        teammateName={teammate.firstName}
      />
      {/* @ts-ignore: */}
      <div style={footerStyle}>
        {!isViewOnly && (
          <NoteField
            initialNote={notes[0]}
            onSaveNote={handleSaveNotes}
            isViewOnly={isViewOnly}
          />
        )}
        <div style={scoreSectionStyle}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              margin: 5,
            }}
          >
            <h1
              className={`score ${isViewOnly ? 'TargetScores--score' : ''}`}
              style={{ margin: 0 }}
            >
              Your score: {score}{' '}
            </h1>
            <p style={{ margin: 0 }}>
              {teammate.firstName}'s score: {teammateScore}
            </p>
            <p style={{ margin: 0 }}>Team score: {score + teammateScore}</p>
          </div>
          {!_.isNaN(score) && <Flower score={score} />}
        </div>
        <div>
          <div>
            <HighFiveText
              highFives={highFivesSentToday}
              player={player}
              teammate={teammate}
            />
            {!isViewOnly ? (
              <div style={footerButtonDivStyle}>
                <div style={highFiveButtonsStyle}>
                  <Button
                    style={{ width: 250, marginRight: 10 }}
                    variant={
                      !playerHasSentHighFiveToday ? 'contained' : 'outlined'
                    }
                    onClick={() => setIsShowingHighFiveDialog(true)}
                  >
                    {!playerHasSentHighFiveToday
                      ? "Send 'em a high five! 👋"
                      : `Chat with ${teammate.firstName}`}
                  </Button>
                </div>
                <Button
                  variant="outlined"
                  onClick={() => setIsShowingTeammateDialog(true)}
                >
                  👀
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {isShowingHighFiveDialog && teammate && (
        <HighFiveDialog
          onSend={handleSendHighFive}
          onCancel={() => setIsShowingHighFiveDialog(false)}
          teammateName={teammate.firstName}
          messages={highFives}
          player={player}
        />
      )}
      {isShowingMiwDialog && isMiwHabit(miwHabit) && (
        <MiwDialog
          targetText={miwHabit.target}
          starterTarget={miwHabit.miwStarterTarget}
          mainTarget={miwHabit.miwMainTarget}
          progress={miwHabit.miwProgress}
          onSubmit={handleMiwSubmit}
          onClose={() => setIsShowingMiwDialog(false)}
        />
      )}
      <Snackbar
        open={isShowingNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
      >
        <Alert onClose={handleCloseNotification} severity="success">
          {notificationText}
        </Alert>
      </Snackbar>
      {isShowingTeammateDialog && teammate && day && (
        <TeammateTargetsDialog
          playerId={teammate.id}
          day={day}
          onClose={() => setIsShowingTeammateDialog(false)}
        />
      )}
    </div>
  )
}
